import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {storage} from "../../utils";
import {addCommas} from "../../utils";
import "./Analytics.css";

const insightLabels = {
    rage_click: "ریج کلیک",
    dead_click: "دد کلیک",
    quick_back: "بازگشت سریع",
    js_error: "خطاهای جاوااسکریپت",
};

const formatNumber = (value) => addCommas(Number(value || 0));

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}٪`;

const SummaryCard = ({title, value, hint}) => (
    <div className="analytics-card analytics-summary-card">
        <span>{title}</span>
        <strong>{value}</strong>
        {hint ? <small>{hint}</small> : null}
    </div>
);

const SectionTitle = ({title, subtitle}) => (
    <div className="analytics-section-title">
        <h2>{title}</h2>
        {subtitle ? <span>{subtitle}</span> : null}
    </div>
);

const BarList = ({title, items, emptyText = "داده‌ای برای نمایش وجود ندارد"}) => {
    const max = useMemo(
        () => Math.max(...(items || []).map((item) => item.count || 0), 0),
        [items]
    );

    return (
        <div className="analytics-card">
            <div className="analytics-card-header">
                <h3>{title}</h3>
            </div>
            {items && items.length ? (
                <div className="analytics-bars">
                    {items.map((item) => (
                        <div className="analytics-bar-row" key={`${title}-${item.label}`}>
                            <div className="analytics-bar-meta">
                                <span title={item.label}>{item.label || "unknown"}</span>
                                <b>{formatNumber(item.count)}</b>
                            </div>
                            <div className="analytics-bar-track">
                                <div
                                    className="analytics-bar-fill"
                                    style={{width: `${max ? (item.count / max) * 100 : 0}%`}}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="analytics-empty">{emptyText}</div>
            )}
        </div>
    );
};

const InsightList = ({items}) => (
    <div className="analytics-card">
        <div className="analytics-card-header">
            <h3>رفتارهای مهم</h3>
        </div>
        <div className="analytics-insights">
            {(items || []).map((item) => (
                <div className="analytics-insight-row" key={item.event_type}>
                    <div>
                        <span>{insightLabels[item.event_type] || item.event_type}</span>
                        <small>{formatNumber(item.count)} رخداد</small>
                    </div>
                    <strong>{formatPercent(item.percent)}</strong>
                </div>
            ))}
        </div>
    </div>
);

const Trend = ({items}) => {
    const max = useMemo(
        () => Math.max(...(items || []).map((item) => item.page_views || 0), 0),
        [items]
    );

    return (
        <div className="analytics-card analytics-trend-card">
            <div className="analytics-card-header">
                <h3>روند بازدید صفحات</h3>
            </div>
            {items && items.length ? (
                <div className="analytics-trend">
                    {items.map((item) => (
                        <div className="analytics-trend-item" key={item.date}>
                            <div
                                className="analytics-trend-column"
                                style={{height: `${max ? Math.max((item.page_views / max) * 100, 8) : 8}%`}}
                                title={`${item.date}: ${formatNumber(item.page_views)} بازدید`}
                            />
                            <span>{item.date.slice(5)}</span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="analytics-empty">هنوز بازدیدی برای این بازه ثبت نشده است.</div>
            )}
        </div>
    );
};

const Analytics = () => {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardSource, setDashboardSource] = useState("loading");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);

        storage
            .load("token")
            .then((token) =>
                axios({
                    method: "get",
                    url: `/analytics/report?days=${days}`,
                    headers: {
                        "x-auth": token,
                    },
                })
            )
            .then((response) => {
                if (!mounted) return;
                setData(response.data?.data || null);
                setLoading(false);
            })
            .catch(() => {
                if (!mounted) return;
                setError("گزارش آنالیتیکس دریافت نشد.");
                setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [days]);

    useEffect(() => {
        let mounted = true;

        storage
            .load("token")
            .then((token) => {
                const headers = {
                    "x-auth": token,
                };

                return axios({
                    method: "get",
                    url: "/production_info",
                    headers,
                })
                    .then((response) => ({
                        response,
                        source: response.data?.data?.source || "production",
                    }))
                    .catch(() =>
                        axios({
                            method: "get",
                            url: "/get_info",
                            headers,
                        }).then((response) => ({
                            response,
                            source: "local",
                        }))
                    );
            })
            .then(({response, source}) => {
                if (!mounted) return;
                setDashboardData(response.data?.data || null);
                setDashboardSource(source);
            })
            .catch(() => {
                if (!mounted) return;
                setDashboardData(null);
                setDashboardSource("error");
            });

        return () => {
            mounted = false;
        };
    }, []);

    const summary = data?.summary || {};
    const monthTransactions =
        dashboardData?.total_previous_month_transactions &&
        dashboardData.total_previous_month_transactions[0]
            ? dashboardData.total_previous_month_transactions[0]._price
            : 0;

    return (
        <div className="analytics-page">
            <div className="analytics-header">
                <div>
                    <h1>آنالیتیکس سایت</h1>
                    <p>گزارش رفتار کاربران سایت اصلی بر اساس داده‌های جمع‌آوری‌شده‌ی داخلی</p>
                </div>
                <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
                    <option value={1}>امروز</option>
                    <option value={7}>۷ روز اخیر</option>
                    <option value={30}>۳۰ روز اخیر</option>
                    <option value={90}>۹۰ روز اخیر</option>
                </select>
            </div>

            {error ? <div className="analytics-error">{error}</div> : null}

            <SectionTitle
                title="خلاصه داشبورد اصلی"
                subtitle={
                    dashboardSource === "production"
                        ? "داده‌های مدیریتی خوانده‌شده از دیتابیس اصلی سایت"
                        : dashboardSource === "local"
                            ? "فعلا داده‌های لوکال نمایش داده می‌شود؛ اتصال production در دسترس نیست"
                        : "داده‌های مدیریتی صفحه داشبورد اصلی"
                }
            />
            <div className={`analytics-source-badge analytics-source-${dashboardSource}`}>
                {dashboardSource === "production"
                    ? "منبع: دیتابیس اصلی"
                    : dashboardSource === "local"
                        ? "منبع: لوکال"
                        : dashboardSource === "error"
                            ? "منبع داده در دسترس نیست"
                            : "در حال بررسی منبع داده"}
            </div>
            <div className="analytics-summary-grid analytics-business-grid">
                <SummaryCard title="کل کاربران" value={formatNumber(dashboardData?.user_count)} />
                <SummaryCard
                    title="ثبت‌نام امروز"
                    value={formatNumber(dashboardData?.registered_users_today)}
                />
                <SummaryCard title="تراکنش‌ها" value={formatNumber(dashboardData?.payment_count)} />
                <SummaryCard title="کامنت‌ها" value={formatNumber(dashboardData?.comment_count)} />
                <SummaryCard
                    title="تراکنش‌های بررسی‌نشده"
                    value={formatNumber(dashboardData?.unverified_payments)}
                />
                <SummaryCard
                    title="مبلغ موفق این ماه"
                    value={`${formatNumber(monthTransactions)} تومان`}
                />
                <SummaryCard
                    title="تماس‌های بی‌پاسخ"
                    value={formatNumber(dashboardData?.unanswered_calls)}
                />
            </div>

            <SectionTitle
                title="رفتار کاربران سایت"
                subtitle="داده‌های جمع‌آوری‌شده از tracker سایت اصلی"
            />
            <div className="analytics-summary-grid">
                <SummaryCard title="نشست‌ها" value={formatNumber(summary.sessions)} />
                <SummaryCard title="کاربران یکتا" value={formatNumber(summary.visitors)} />
                <SummaryCard title="بازدید صفحات" value={formatNumber(summary.page_views)} />
                <SummaryCard title="کلیک‌ها" value={formatNumber(summary.clicks)} />
                <SummaryCard
                    title="میانگین زمان نشست"
                    value={`${formatNumber(summary.average_session_seconds)} ثانیه`}
                />
                <SummaryCard
                    title="میانگین عمق اسکرول"
                    value={formatPercent(summary.average_scroll_depth)}
                />
            </div>

            {loading ? (
                <div className="analytics-loading">در حال دریافت گزارش...</div>
            ) : (
                <>
                    <div className="analytics-main-grid">
                        <Trend items={data?.trends || []} />
                        <InsightList items={data?.insights || []} />
                    </div>
                    <div className="analytics-grid">
                        <BarList title="صفحات پربازدید" items={data?.top_pages || []} />
                        <BarList title="دیوایس‌ها" items={data?.devices || []} />
                        <BarList title="مرورگرها" items={data?.browsers || []} />
                        <BarList title="سیستم‌عامل‌ها" items={data?.operating_systems || []} />
                        <BarList title="کانال‌های ورودی" items={data?.channels || []} />
                        <BarList title="صفحات ورود" items={data?.entry_pages || []} />
                    </div>
                </>
            )}
        </div>
    );
};

export {Analytics};
